import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import hostlink from "../Hostlink/hostlink";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// npm install xlsx jspdf jspdf-autotable
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const EmployeeAttendanceReport = () => {

    // =========================================================
    // SHOP ID
    // =========================================================
    const getShopId = () => {
        try {
            const user = localStorage.getItem("MYPCKARTUSER");
            if (!user) return "0";

            const parsed = JSON.parse(user);
            return parsed?.[0]?.id || "0";
        } catch (error) {
            console.error("Shop ID error:", error);
            return "0";
        }
    };

    const shopid = getShopId();

    // =========================================================
    // MONTH
    // =========================================================
    const now = new Date();

    const getCurrentMonth = () =>
        `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

    const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());

    // =========================================================
    // STATES
    // =========================================================
    const [employees, setEmployees] = useState([]);
    const [attendanceRecords, setAttendanceRecords] = useState([]);

    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [loadingAttendance, setLoadingAttendance] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [exporting, setExporting] = useState("");

    // =========================================================
    // LOAD EMPLOYEES
    // =========================================================
    const loadEmployees = async () => {
        try {
            setLoadingEmployees(true);

            const response = await axios.get(
                `${hostlink}/list/${shopid}`
            );

            setEmployees(
                Array.isArray(response.data) ? response.data : []
            );
        } catch (error) {
            console.error(error);
            setEmployees([]);
            toast.error("Unable to load employees");
        } finally {
            setLoadingEmployees(false);
        }
    };

    // =========================================================
    // LOAD ATTENDANCE
    // =========================================================
    const loadAttendance = async () => {
        try {
            if (!selectedMonth) return;

            const [year, month] = selectedMonth.split("-");

            setLoadingAttendance(true);

            const response = await axios.get(
                `${hostlink}/employee-attendance/month`,
                {
                    params: {
                        shopid,
                        month: Number(month),
                        year: Number(year)
                    }
                }
            );

            setAttendanceRecords(
                Array.isArray(response.data) ? response.data : []
            );
        } catch (error) {
            console.error(error);
            setAttendanceRecords([]);
            toast.error("Unable to load attendance");
        } finally {
            setLoadingAttendance(false);
        }
    };

    useEffect(() => {
        if (shopid && shopid !== "0") {
            loadEmployees();
        }
    }, [shopid]);

    useEffect(() => {
        if (shopid && shopid !== "0") {
            loadAttendance();
        }
    }, [selectedMonth, shopid]);

    // =========================================================
    // MONTH INFO
    // =========================================================
    const monthInfo = useMemo(() => {
        const [year, month] = selectedMonth.split("-").map(Number);

        const days = new Date(year, month, 0).getDate();

        return {
            year,
            month,
            days
        };
    }, [selectedMonth]);

    // =========================================================
    // DAY LIST
    // =========================================================
    const days = useMemo(() => {
        const result = [];

        for (let day = 1; day <= monthInfo.days; day++) {
            const dateObject = new Date(
                monthInfo.year,
                monthInfo.month - 1,
                day
            );

            result.push({
                day,
                date:
                    `${monthInfo.year}-${String(monthInfo.month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
                dayName: dateObject.toLocaleDateString("en-IN", {
                    weekday: "short"
                })
            });
        }

        return result;
    }, [monthInfo]);

    // =========================================================
    // ATTENDANCE MAP
    // =========================================================
    const attendanceMap = useMemo(() => {
        const map = {};

        attendanceRecords.forEach((item) => {
            if (!item.employeeid || !item.attendancedate) return;

            map[`${item.employeeid}_${item.attendancedate}`] = item;
        });

        return map;
    }, [attendanceRecords]);

    const getAttendance = (employeeId, date) =>
        attendanceMap[`${employeeId}_${date}`] || null;

    // =========================================================
    // FILTER
    // =========================================================
    const filteredEmployees = useMemo(() => {
        const search = searchText.trim().toLowerCase();

        if (!search) return employees;

        return employees.filter((employee) => {
            const name = String(employee.accname || "").toLowerCase();
            const mobile = String(employee.mobno || "").toLowerCase();
            const id = String(employee.id || "").toLowerCase();

            return (
                name.includes(search) ||
                mobile.includes(search) ||
                id.includes(search)
            );
        });
    }, [employees, searchText]);

    // =========================================================
    // STATUS
    // =========================================================
    const normalizeStatus = (record) =>
        String(record?.status || "").toUpperCase().trim();

    const getStatusCode = (record) => {
        switch (normalizeStatus(record)) {
            case "PRESENT": return "P";
            case "ABSENT": return "A";
            case "HOLIDAY": return "H";
            case "LEAVE": return "L";
            case "HALF_DAY": return "HD";
            default: return "-";
        }
    };

    const getStatusClass = (record) => {
        switch (normalizeStatus(record)) {
            case "PRESENT": return "status-present";
            case "ABSENT": return "status-absent";
            case "HOLIDAY": return "status-holiday";
            case "LEAVE": return "status-leave";
            case "HALF_DAY": return "status-half";
            default: return "status-empty";
        }
    };

    const getStatusLabel = (record) => {
        switch (normalizeStatus(record)) {
            case "PRESENT": return "Present";
            case "ABSENT": return "Absent";
            case "HOLIDAY": return "Holiday";
            case "LEAVE": return "Leave";
            case "HALF_DAY": return "Half Day";
            default: return "Not Marked";
        }
    };

    // =========================================================
    // TOTALS
    // =========================================================
    const getEmployeeTotals = (employeeId) => {
        const totals = {
            present: 0,
            absent: 0,
            holiday: 0,
            leave: 0,
            halfday: 0,
            notMarked: 0
        };

        days.forEach((day) => {
            const record = getAttendance(employeeId, day.date);
            const status = normalizeStatus(record);

            switch (status) {
                case "PRESENT":
                    totals.present++;
                    break;
                case "ABSENT":
                    totals.absent++;
                    break;
                case "HOLIDAY":
                    totals.holiday++;
                    break;
                case "LEAVE":
                    totals.leave++;
                    break;
                case "HALF_DAY":
                    totals.halfday++;
                    break;
                default:
                    totals.notMarked++;
            }
        });

        return totals;
    };

    const overallTotals = useMemo(() => {
        const totals = {
            present: 0,
            absent: 0,
            holiday: 0,
            leave: 0,
            halfday: 0,
            notMarked: 0
        };

        employees.forEach((employee) => {
            const employeeTotals = getEmployeeTotals(employee.id);

            Object.keys(totals).forEach((key) => {
                totals[key] += employeeTotals[key];
            });
        });

        return totals;
    }, [employees, days, attendanceMap]);

    const monthDisplay = useMemo(() => {
        return new Date(
            monthInfo.year,
            monthInfo.month - 1,
            1
        ).toLocaleDateString("en-IN", {
            month: "long",
            year: "numeric"
        });
    }, [monthInfo]);

    // =========================================================
    // TIME DISPLAY
    // =========================================================
    const formatTime = (time) => {
    if (!time) return "";

    const value = String(time).trim();

    if (!value) return "";

    const parts = value.split(":");

    if (parts.length < 2) {
        return value;
    }

    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];

    if (isNaN(hours)) {
        return value;
    }

    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12;

    if (hours === 0) {
        hours = 12;
    }

    return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
};

    const getCellTitle = (record, date) => {
        if (!record) return `${date}\nNot Marked`;

        let text = `${date}\n${getStatusLabel(record)}`;

        if (record.intime) text += `\nIn: ${formatTime(record.intime)}`;
        if (record.outtime) text += `\nOut: ${formatTime(record.outtime)}`;
        if (record.narration) text += `\n${record.narration}`;

        return text;
    };

    // =========================================================
    // REFRESH
    // =========================================================
    const handleRefresh = async () => {
        await Promise.all([
            loadEmployees(),
            loadAttendance()
        ]);

        toast.success("Report refreshed");
    };

    // =========================================================
    // EXCEL EXPORT
    // =========================================================
    const exportExcel = () => {
        try {
            setExporting("excel");

            const rows = [];

            // Header information
            rows.push(["EMPLOYEE ATTENDANCE REPORT"]);
            rows.push([`Month: ${monthDisplay}`]);
            rows.push([`Shop ID: ${shopid}`]);
            rows.push([]);

            // Main header
            const header = [
                "Employee ID",
                "Employee Name",
                "Mobile"
            ];

            days.forEach((day) => {
                header.push(`${String(day.day).padStart(2, "0")} ${day.dayName}`);
            });

            header.push("Present");
            header.push("Absent");
            header.push("Holiday");
            header.push("Leave");
            header.push("Half Day");
            header.push("Not Marked");

            rows.push(header);

            // Data
            filteredEmployees.forEach((employee) => {
                const row = [
                    employee.id || "",
                    employee.accname || "",
                    employee.mobno || ""
                ];

                days.forEach((day) => {
                    const record = getAttendance(employee.id, day.date);

                    if (!record) {
                        row.push("-");
                        return;
                    }

                    const code = getStatusCode(record);

                    let value = code;

                    if (
                        normalizeStatus(record) === "PRESENT" &&
                        (record.intime || record.outtime)
                    ) {
                        value =
                            `${code} | In ${formatTime(record.intime) || "-"} | Out ${formatTime(record.outtime) || "-"}`;
                    }

                    if (
                        normalizeStatus(record) === "HOLIDAY" &&
                        record.narration
                    ) {
                        value = `${code} | ${record.narration}`;
                    }

                    row.push(value);
                });

                const totals = getEmployeeTotals(employee.id);

                row.push(totals.present);
                row.push(totals.absent);
                row.push(totals.holiday);
                row.push(totals.leave);
                row.push(totals.halfday);
                row.push(totals.notMarked);

                rows.push(row);
            });

            rows.push([]);
            rows.push(["OVERALL SUMMARY"]);
            rows.push(["Present", overallTotals.present]);
            rows.push(["Absent", overallTotals.absent]);
            rows.push(["Holiday", overallTotals.holiday]);
            rows.push(["Leave", overallTotals.leave]);
            rows.push(["Half Day", overallTotals.halfday]);
            rows.push(["Not Marked", overallTotals.notMarked]);

            const worksheet = XLSX.utils.aoa_to_sheet(rows);

            // Column widths
            worksheet["!cols"] = [
                { wch: 12 },
                { wch: 24 },
                { wch: 15 },
                ...days.map(() => ({ wch: 22 })),
                { wch: 10 },
                { wch: 10 },
                { wch: 10 },
                { wch: 10 },
                { wch: 10 },
                { wch: 12 }
            ];

            // Merge title
            worksheet["!merges"] = [
                {
                    s: { r: 0, c: 0 },
                    e: { r: 0, c: header.length - 1 }
                }
            ];

            const workbook = XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                "Attendance"
            );

            XLSX.writeFile(
                workbook,
                `Employee_Attendance_${selectedMonth}.xlsx`
            );

            toast.success("Excel exported successfully");

        } catch (error) {
            console.error("Excel export error:", error);
            toast.error("Excel export failed");
        } finally {
            setExporting("");
        }
    };

    // =========================================================
    // PDF EXPORT
    // =========================================================
    const exportPDF = () => {
        try {
            setExporting("pdf");

            const doc = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: "a3"
            });

            const pageWidth = doc.internal.pageSize.getWidth();

            doc.setFontSize(18);
            doc.setFont("helvetica", "bold");
            doc.text(
                "Employee Attendance Report",
                12,
                14
            );

            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");

            doc.text(
                `Month: ${monthDisplay}`,
                12,
                21
            );

            doc.text(
                `Employees: ${filteredEmployees.length}`,
                pageWidth - 55,
                21
            );

            const head = [
                [
                    "ID",
                    "Employee",
                    ...days.map(
                        (day) =>
                            `${String(day.day).padStart(2, "0")}\n${day.dayName}`
                    ),
                    "P",
                    "A",
                    "H",
                    "L",
                    "HD"
                ]
            ];

            const body = filteredEmployees.map((employee) => {

                const row = [
                    String(employee.id || ""),
                    String(employee.accname || "")
                ];

                days.forEach((day) => {
                    const record =
                        getAttendance(employee.id, day.date);

                    if (!record) {
                        row.push("-");
                        return;
                    }

                    let value = getStatusCode(record);

                    if (normalizeStatus(record) === "PRESENT") {
                        const inTime = formatTime(record.intime);
                        const outTime = formatTime(record.outtime);

                        if (inTime || outTime) {
                            value =
                                `${value}\n${inTime || "-"}-${outTime || "-"}`;
                        }
                    }

                    row.push(value);
                });

                const totals = getEmployeeTotals(employee.id);

                row.push(totals.present);
                row.push(totals.absent);
                row.push(totals.holiday);
                row.push(totals.leave);
                row.push(totals.halfday);

                return row;
            });

            autoTable(doc, {
                startY: 27,
                head,
                body,
                theme: "grid",

                styles: {
                    fontSize: 6,
                    cellPadding: 1.5,
                    halign: "center",
                    valign: "middle",
                    lineWidth: 0.1
                },

                headStyles: {
                    fontSize: 6,
                    fontStyle: "bold",
                    halign: "center",
                    valign: "middle"
                },

                columnStyles: {
                    0: {
                        cellWidth: 10
                    },
                    1: {
                        cellWidth: 34,
                        halign: "left"
                    }
                },

                didParseCell: (data) => {
                    if (data.section !== "body") return;

                    const text =
                        String(data.cell.raw || "");

                    if (text.startsWith("P")) {
                        data.cell.styles.fontStyle = "bold";
                    }

                    if (text === "A") {
                        data.cell.styles.fontStyle = "bold";
                    }

                    if (text === "H") {
                        data.cell.styles.fontStyle = "bold";
                    }

                    if (text === "L") {
                        data.cell.styles.fontStyle = "bold";
                    }

                    if (text === "HD") {
                        data.cell.styles.fontStyle = "bold";
                    }
                },

                didDrawPage: (data) => {
                    const pageHeight =
                        doc.internal.pageSize.getHeight();

                    doc.setFontSize(7);

                    doc.text(
                        `Generated: ${new Date().toLocaleString("en-IN")}`,
                        12,
                        pageHeight - 6
                    );

                    doc.text(
                        `Page ${doc.getNumberOfPages()}`,
                        pageWidth - 25,
                        pageHeight - 6
                    );
                }
            });

            // Summary page/section
            doc.addPage();

            doc.setFontSize(16);
            doc.setFont("helvetica", "bold");
            doc.text(
                `${monthDisplay} - Attendance Summary`,
                12,
                16
            );

            const summaryBody = [
                ["Total Employees", String(employees.length)],
                ["Present", String(overallTotals.present)],
                ["Absent", String(overallTotals.absent)],
                ["Holiday", String(overallTotals.holiday)],
                ["Leave", String(overallTotals.leave)],
                ["Half Day", String(overallTotals.halfday)],
                ["Not Marked", String(overallTotals.notMarked)]
            ];

            autoTable(doc, {
                startY: 24,
                head: [["Summary", "Count"]],
                body: summaryBody,
                theme: "grid",
                styles: {
                    fontSize: 10,
                    cellPadding: 4
                },
                headStyles: {
                    fontStyle: "bold"
                },
                columnStyles: {
                    0: { cellWidth: 70 },
                    1: {
                        cellWidth: 35,
                        halign: "center"
                    }
                }
            });

            doc.save(
                `Employee_Attendance_${selectedMonth}.pdf`
            );

            toast.success("PDF exported successfully");

        } catch (error) {
            console.error("PDF export error:", error);
            toast.error(
                "PDF export failed. Check jspdf-autotable installation."
            );
        } finally {
            setExporting("");
        }
    };

    // =========================================================
    // LOADING
    // =========================================================
    const loading =
        loadingEmployees ||
        loadingAttendance;

    // =========================================================
    // UI
    // =========================================================
    return (
        <div className="attendance-report-page">

            <ToastContainer
                position="top-right"
                autoClose={2000}
            />

            {/* HEADER */}
            <div className="top-header">

                <div className="title-area">

                    <div className="title-icon">
                        <i className="bi bi-calendar2-check"></i>
                    </div>

                    <div>
                        <h1>Attendance Report</h1>

                        <p>
                            Employee attendance • {monthDisplay}
                        </p>
                    </div>

                </div>

                <div className="header-buttons">

                    <button
                        className="btn btn-light action-btn"
                        onClick={handleRefresh}
                        disabled={loading}
                    >
                        <i className="bi bi-arrow-clockwise"></i>
                        Refresh
                    </button>

                    <button
                        className="btn btn-outline-success action-btn"
                        onClick={exportExcel}
                        disabled={loading || exporting === "excel"}
                    >
                        <i className="bi bi-file-earmark-excel"></i>
                        {exporting === "excel"
                            ? "Exporting..."
                            : "Excel"}
                    </button>

                    <button
                        className="btn btn-dark action-btn"
                        onClick={exportPDF}
                        disabled={loading || exporting === "pdf"}
                    >
                        <i className="bi bi-file-earmark-pdf"></i>
                        {exporting === "pdf"
                            ? "Exporting..."
                            : "PDF"}
                    </button>

                </div>

            </div>


            {/* FILTER */}
            <div className="filter-panel">

                <div className="filter-group">

                    <label>
                        <i className="bi bi-calendar3"></i>
                        Select Month
                    </label>

                    <input
                        type="month"
                        className="form-control month-input"
                        value={selectedMonth}
                        onChange={(e) =>
                            setSelectedMonth(e.target.value)
                        }
                    />

                </div>

                <div className="filter-group employee-search">

                    <label>
                        <i className="bi bi-search"></i>
                        Search Employee
                    </label>

                    <input
                        type="text"
                        className="form-control"
                        placeholder="Name, mobile or employee ID"
                        value={searchText}
                        onChange={(e) =>
                            setSearchText(e.target.value)
                        }
                    />

                </div>

                <div className="report-period">

                    <span>REPORT PERIOD</span>

                    <strong>{monthDisplay}</strong>

                    <small>
                        {days.length} days
                    </small>

                </div>

            </div>


            {/* SUMMARY */}
            <div className="summary-row">

                <div className="summary-card employee-card">
                    <div className="summary-card-icon">
                        <i className="bi bi-people"></i>
                    </div>
                    <div>
                        <span>Employees</span>
                        <strong>{employees.length}</strong>
                    </div>
                </div>

                <div className="summary-card present-card">
                    <div className="summary-card-icon">
                        P
                    </div>
                    <div>
                        <span>Present</span>
                        <strong>{overallTotals.present}</strong>
                    </div>
                </div>

                <div className="summary-card absent-card">
                    <div className="summary-card-icon">
                        A
                    </div>
                    <div>
                        <span>Absent</span>
                        <strong>{overallTotals.absent}</strong>
                    </div>
                </div>

                <div className="summary-card holiday-card">
                    <div className="summary-card-icon">
                        H
                    </div>
                    <div>
                        <span>Holiday</span>
                        <strong>{overallTotals.holiday}</strong>
                    </div>
                </div>

                <div className="summary-card leave-card">
                    <div className="summary-card-icon">
                        L
                    </div>
                    <div>
                        <span>Leave</span>
                        <strong>{overallTotals.leave}</strong>
                    </div>
                </div>

                <div className="summary-card half-card">
                    <div className="summary-card-icon">
                        HD
                    </div>
                    <div>
                        <span>Half Day</span>
                        <strong>{overallTotals.halfday}</strong>
                    </div>
                </div>

            </div>


            {/* LEGEND */}
            <div className="legend-panel">

                <div className="legend-heading">
                    Attendance
                </div>

                <div className="legend-items">

                    <span>
                        <b className="legend-status status-present">P</b>
                        Present
                    </span>

                    <span>
                        <b className="legend-status status-absent">A</b>
                        Absent
                    </span>

                    <span>
                        <b className="legend-status status-holiday">H</b>
                        Holiday
                    </span>

                    <span>
                        <b className="legend-status status-leave">L</b>
                        Leave
                    </span>

                    <span>
                        <b className="legend-status status-half">HD</b>
                        Half Day
                    </span>

                    <span>
                        <b className="legend-status status-empty">-</b>
                        Not Marked
                    </span>

                </div>

                <div className="time-help">
                    <i className="bi bi-clock"></i>
                    Green cells show In / Out time
                </div>

            </div>


            {/* TABLE */}
            <div className="table-card">

                <div className="table-toolbar">

                    <div>
                        <strong>Monthly Attendance</strong>
                        <span>
                            {filteredEmployees.length} employees
                        </span>
                    </div>

                    {loading && (
                        <div className="loading-text">
                            <span className="spinner-border spinner-border-sm"></span>
                            Loading...
                        </div>
                    )}

                </div>

                <div className="table-scroll">

                    <table className="attendance-table">

                        <thead>
                            <tr>

                                <th className="employee-header">
                                    <div>EMPLOYEE</div>
                                    <small>NAME / ID</small>
                                </th>

                                {days.map((day) => (
                                    <th
                                        key={day.date}
                                        className="day-header"
                                    >
                                        <strong>
                                            {String(day.day).padStart(2, "0")}
                                        </strong>
                                        <small>
                                            {day.dayName}
                                        </small>
                                    </th>
                                ))}

                                <th className="total-header present-text">P</th>
                                <th className="total-header absent-text">A</th>
                                <th className="total-header holiday-text">H</th>
                                <th className="total-header leave-text">L</th>
                                <th className="total-header half-text">HD</th>

                            </tr>
                        </thead>

                        <tbody>

                            {loading ? (

                                <tr>
                                    <td
                                        colSpan={days.length + 6}
                                        className="state-cell"
                                    >
                                        <div className="big-spinner">
                                            <span className="spinner-border"></span>
                                        </div>
                                        Loading attendance...
                                    </td>
                                </tr>

                            ) : filteredEmployees.length === 0 ? (

                                <tr>
                                    <td
                                        colSpan={days.length + 6}
                                        className="state-cell"
                                    >
                                        <i className="bi bi-person-x empty-icon"></i>
                                        <strong>No employees found</strong>
                                    </td>
                                </tr>

                            ) : (

                                filteredEmployees.map((employee) => {

                                    const totals =
                                        getEmployeeTotals(employee.id);

                                    return (
                                        <tr key={employee.id}>

                                            <td className="employee-cell">

                                                <div className="employee-profile">

                                                    <div className="avatar">
                                                        {String(
                                                            employee.accname ||
                                                            "E"
                                                        )
                                                            .charAt(0)
                                                            .toUpperCase()}
                                                    </div>

                                                    <div className="employee-details">

                                                        <strong>
                                                            {employee.accname ||
                                                                "Employee"}
                                                        </strong>

                                                        <span>
                                                            ID #{employee.id}
                                                            {employee.mobno
                                                                ? ` • ${employee.mobno}`
                                                                : ""}
                                                        </span>

                                                    </div>

                                                </div>

                                            </td>

                                            {days.map((day) => {

                                                const record =
                                                    getAttendance(
                                                        employee.id,
                                                        day.date
                                                    );

                                                const status =
                                                    normalizeStatus(record);

                                                const code =
                                                    getStatusCode(record);

                                                return (
                                                    <td
                                                        key={day.date}
                                                        className="attendance-cell"
                                                        title={getCellTitle(
                                                            record,
                                                            day.date
                                                        )}
                                                    >

                                                        <div
                                                            className={`attendance-status ${getStatusClass(record)}`}
                                                        >

                                                            <strong>
                                                                {code}
                                                            </strong>

                                                            {status === "PRESENT" &&
                                                                (record?.intime ||
                                                                    record?.outtime) && (

                                                                    <div className="cell-time">

                                                                        <span>
                                                                            {formatTime(
                                                                                record.intime
                                                                            ) || "--:--"}
                                                                        </span>

                                                                        <span className="time-arrow">
                                                                            ↓
                                                                        </span>

                                                                        <span>
                                                                            {formatTime(
                                                                                record.outtime
                                                                            ) || "--:--"}
                                                                        </span>

                                                                    </div>
                                                                )}

                                                            {status === "HOLIDAY" &&
                                                                record?.narration && (
                                                                    <div className="holiday-name">
                                                                        {record.narration}
                                                                    </div>
                                                                )}

                                                        </div>

                                                    </td>
                                                );
                                            })}

                                            <td className="total-cell present-total">
                                                {totals.present}
                                            </td>

                                            <td className="total-cell absent-total">
                                                {totals.absent}
                                            </td>

                                            <td className="total-cell holiday-total">
                                                {totals.holiday}
                                            </td>

                                            <td className="total-cell leave-total">
                                                {totals.leave}
                                            </td>

                                            <td className="total-cell half-total">
                                                {totals.halfday}
                                            </td>

                                        </tr>
                                    );
                                })
                            )}

                        </tbody>

                    </table>

                </div>

            </div>


            {/* FOOTER */}
            <div className="report-footer">

                <span>
                    Showing <strong>{filteredEmployees.length}</strong> of{" "}
                    <strong>{employees.length}</strong> employees
                </span>

                <span>
                    Not marked: <strong>{overallTotals.notMarked}</strong>
                </span>

            </div>


            {/* =====================================================
                CSS
            ====================================================== */}
            <style>{`

                * {
                    box-sizing: border-box;
                }

                .attendance-report-page {
                    min-height: 100vh;
                    padding: 20px;
                    background: #f5f7fb;
                    color: #172033;
                    font-family:
                        Inter,
                        -apple-system,
                        BlinkMacSystemFont,
                        "Segoe UI",
                        sans-serif;
                }

                /* HEADER */

                .top-header {
                    background: #ffffff;
                    border: 1px solid #e7ebf1;
                    border-radius: 18px;
                    padding: 18px 20px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 20px;
                    margin-bottom: 14px;
                }

                .title-area {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                }

                .title-icon {
                    width: 46px;
                    height: 46px;
                    border-radius: 13px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #172033;
                    color: #ffffff;
                    font-size: 19px;
                }

                .title-area h1 {
                    font-size: 22px;
                    margin: 0;
                    font-weight: 800;
                    letter-spacing: -.4px;
                }

                .title-area p {
                    margin: 3px 0 0;
                    color: #818a9b;
                    font-size: 12px;
                }

                .header-buttons {
                    display: flex;
                    gap: 7px;
                    flex-wrap: wrap;
                }

                .action-btn {
                    height: 38px;
                    border-radius: 9px;
                    font-size: 12px;
                    font-weight: 700;
                    padding: 0 13px;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }

                /* FILTER */

                .filter-panel {
                    background: #ffffff;
                    border: 1px solid #e7ebf1;
                    border-radius: 16px;
                    padding: 15px 18px;
                    display: flex;
                    align-items: end;
                    gap: 14px;
                    margin-bottom: 14px;
                }

                .filter-group {
                    width: 210px;
                }

                .filter-group.employee-search {
                    width: 300px;
                }

                .filter-group label {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    font-size: 11px;
                    font-weight: 750;
                    color: #687386;
                    margin-bottom: 6px;
                }

                .filter-group label i {
                    color: #8c95a5;
                }

                .filter-group .form-control {
                    height: 39px;
                    border-radius: 9px;
                    border-color: #dfe4eb;
                    font-size: 12px;
                }

                .report-period {
                    margin-left: auto;
                    text-align: right;
                    padding-right: 4px;
                }

                .report-period span {
                    display: block;
                    font-size: 9px;
                    color: #98a0ae;
                    letter-spacing: 1px;
                    font-weight: 700;
                }

                .report-period strong {
                    display: block;
                    font-size: 17px;
                    margin-top: 2px;
                }

                .report-period small {
                    color: #8b94a4;
                    font-size: 10px;
                }

                /* SUMMARY */

                .summary-row {
                    display: grid;
                    grid-template-columns: repeat(6, 1fr);
                    gap: 10px;
                    margin-bottom: 14px;
                }

                .summary-card {
                    background: #ffffff;
                    border: 1px solid #e7ebf1;
                    border-radius: 14px;
                    min-height: 76px;
                    padding: 12px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .summary-card-icon {
                    width: 39px;
                    height: 39px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f0f2f6;
                    color: #515b6c;
                    font-size: 12px;
                    font-weight: 850;
                    flex-shrink: 0;
                }

                .summary-card span {
                    display: block;
                    color: #8992a2;
                    font-size: 10px;
                    margin-bottom: 2px;
                }

                .summary-card strong {
                    display: block;
                    font-size: 20px;
                    line-height: 1;
                    font-weight: 800;
                }

                .present-card .summary-card-icon {
                    background: #e5f8ee;
                    color: #11894a;
                }

                .absent-card .summary-card-icon {
                    background: #ffe9e9;
                    color: #d13939;
                }

                .holiday-card .summary-card-icon {
                    background: #fff3d3;
                    color: #ad7300;
                }

                .leave-card .summary-card-icon {
                    background: #eee8ff;
                    color: #6945bb;
                }

                .half-card .summary-card-icon {
                    background: #e6f3ff;
                    color: #176db8;
                }

                /* LEGEND */

                .legend-panel {
                    background: #ffffff;
                    border: 1px solid #e7ebf1;
                    border-radius: 13px;
                    padding: 10px 14px;
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    margin-bottom: 14px;
                }

                .legend-heading {
                    font-size: 11px;
                    font-weight: 800;
                    color: #4e586a;
                }

                .legend-items {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                    flex-wrap: wrap;
                }

                .legend-items span {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    font-size: 10px;
                    color: #737d8f;
                }

                .legend-status {
                    width: 23px;
                    height: 23px;
                    border-radius: 6px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 8px;
                }

                .time-help {
                    margin-left: auto;
                    color: #7c8697;
                    font-size: 10px;
                    white-space: nowrap;
                }

                .time-help i {
                    margin-right: 4px;
                }

                /* TABLE */

                .table-card {
                    background: #ffffff;
                    border: 1px solid #e4e8ef;
                    border-radius: 17px;
                    overflow: hidden;
                }

                .table-toolbar {
                    min-height: 50px;
                    padding: 0 15px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid #e9edf2;
                }

                .table-toolbar strong {
                    font-size: 13px;
                    font-weight: 800;
                }

                .table-toolbar span {
                    margin-left: 8px;
                    color: #929aaa;
                    font-size: 10px;
                }

                .loading-text {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    color: #7d8798;
                    font-size: 10px;
                }

                .table-scroll {
                    overflow: auto;
                    max-height: 650px;
                }

                .attendance-table {
                    border-collapse: separate;
                    border-spacing: 0;
                    width: max-content;
                    min-width: 100%;
                }

                .attendance-table th {
                    position: sticky;
                    top: 0;
                    z-index: 5;
                    background: #f8f9fb;
                    border-right: 1px solid #e7ebf0;
                    border-bottom: 1px solid #dfe4eb;
                    height: 48px;
                    padding: 5px;
                    text-align: center;
                }

                .employee-header {
                    position: sticky !important;
                    left: 0;
                    z-index: 8 !important;
                    width: 235px;
                    min-width: 235px;
                    text-align: left !important;
                    padding-left: 15px !important;
                }

                .employee-header div {
                    font-size: 10px;
                    font-weight: 800;
                    color: #434c5d;
                }

                .employee-header small {
                    color: #9aa2b0;
                    font-size: 8px;
                }

                .day-header {
                    width: 61px;
                    min-width: 61px;
                }

                .day-header strong {
                    display: block;
                    color: #30394b;
                    font-size: 11px;
                    line-height: 13px;
                }

                .day-header small {
                    color: #969eac;
                    font-size: 8px;
                    font-weight: 600;
                }

                .total-header {
                    width: 45px;
                    min-width: 45px;
                    font-size: 10px !important;
                    font-weight: 850 !important;
                }

                .attendance-table tbody td {
                    border-right: 1px solid #edf0f4;
                    border-bottom: 1px solid #edf0f4;
                }

                .employee-cell {
                    position: sticky;
                    left: 0;
                    z-index: 3;
                    background: #ffffff;
                    width: 235px;
                    min-width: 235px;
                    padding: 7px 12px;
                }

                .employee-profile {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                }

                .avatar {
                    width: 34px;
                    height: 34px;
                    border-radius: 9px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #edf0f5;
                    color: #525c6d;
                    font-size: 12px;
                    font-weight: 800;
                    flex-shrink: 0;
                }

                .employee-details strong {
                    display: block;
                    max-width: 175px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    color: #293142;
                    font-size: 11px;
                }

                .employee-details span {
                    display: block;
                    color: #9aa2b0;
                    font-size: 8px;
                    margin-top: 2px;
                }

                .attendance-cell {
                    width: 61px;
                    min-width: 61px;
                    height: 61px;
                    text-align: center;
                    padding: 3px;
                    background: #ffffff;
                }

                .attendance-status {
                    width: 55px;
                    min-height: 53px;
                    margin: auto;
                    border-radius: 9px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    cursor: default;
                    transition: transform .12s ease;
                }

                .attendance-status:hover {
                    transform: scale(1.05);
                }

                .attendance-status > strong {
                    font-size: 10px;
                    line-height: 12px;
                    font-weight: 900;
                }

                .cell-time {
                    margin-top: 3px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    font-size: 7px;
                    line-height: 9px;
                    font-weight: 650;
                    opacity: .78;
                }

                .time-arrow {
                    font-size: 7px;
                    opacity: .65;
                }

                .holiday-name {
                    max-width: 50px;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    font-size: 6px;
                    margin-top: 2px;
                    opacity: .75;
                }

                /* STATUS */

                .status-present {
                    background: #e6f8ee !important;
                    color: #12884a !important;
                }

                .status-absent {
                    background: #ffe9e9 !important;
                    color: #d03737 !important;
                }

                .status-holiday {
                    background: #fff2d0 !important;
                    color: #ad7300 !important;
                }

                .status-leave {
                    background: #eee8ff !important;
                    color: #6945bc !important;
                }

                .status-half {
                    background: #e5f2ff !important;
                    color: #176db9 !important;
                }

                .status-empty {
                    background: #f1f3f6 !important;
                    color: #a1a8b4 !important;
                }

                /* TOTALS */

                .total-cell {
                    width: 45px;
                    min-width: 45px;
                    text-align: center;
                    font-size: 11px;
                    font-weight: 800;
                    background: #fafbfc;
                }

                .present-text,
                .present-total {
                    color: #12884a !important;
                }

                .absent-text,
                .absent-total {
                    color: #d03737 !important;
                }

                .holiday-text,
                .holiday-total {
                    color: #ad7300 !important;
                }

                .leave-text,
                .leave-total {
                    color: #6945bc !important;
                }

                .half-text,
                .half-total {
                    color: #176db9 !important;
                }

                .present-total {
                    background: #f3fcf7;
                }

                .absent-total {
                    background: #fff7f7;
                }

                .holiday-total {
                    background: #fffdf6;
                }

                .leave-total {
                    background: #faf8ff;
                }

                .half-total {
                    background: #f5faff;
                }

                /* STATE */

                .state-cell {
                    padding: 55px !important;
                    text-align: center;
                    color: #7e8797;
                    font-size: 12px;
                }

                .big-spinner {
                    margin-bottom: 10px;
                }

                .empty-icon {
                    display: block;
                    font-size: 30px;
                    margin-bottom: 8px;
                    color: #aab1bd;
                }

                /* FOOTER */

                .report-footer {
                    display: flex;
                    justify-content: space-between;
                    margin-top: 9px;
                    padding: 0 3px;
                    color: #8a93a3;
                    font-size: 10px;
                }

                .report-footer strong {
                    color: #4c5566;
                }

                /* SCROLLBAR */

                .table-scroll::-webkit-scrollbar {
                    width: 9px;
                    height: 9px;
                }

                .table-scroll::-webkit-scrollbar-track {
                    background: #f1f3f6;
                }

                .table-scroll::-webkit-scrollbar-thumb {
                    background: #c8ced8;
                    border-radius: 20px;
                }

                /* RESPONSIVE */

                @media (max-width: 1200px) {
                    .summary-row {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }

                @media (max-width: 800px) {

                    .attendance-report-page {
                        padding: 10px;
                    }

                    .top-header {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .header-buttons {
                        width: 100%;
                    }

                    .action-btn {
                        flex: 1;
                        justify-content: center;
                    }

                    .filter-panel {
                        align-items: stretch;
                        flex-direction: column;
                    }

                    .filter-group,
                    .filter-group.employee-search {
                        width: 100%;
                    }

                    .report-period {
                        margin-left: 0;
                        text-align: left;
                    }

                    .legend-panel {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .time-help {
                        margin-left: 0;
                    }

                    .summary-row {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }

                @media (max-width: 450px) {

                    .summary-card {
                        padding: 10px;
                    }

                    .summary-card strong {
                        font-size: 17px;
                    }

                    .summary-card-icon {
                        width: 35px;
                        height: 35px;
                    }

                }

                /* PRINT FROM BROWSER */

                @media print {

                    .attendance-report-page {
                        padding: 0;
                        background: #ffffff;
                    }

                    .top-header,
                    .filter-panel,
                    .summary-row,
                    .legend-panel,
                    .table-toolbar,
                    .report-footer {
                        display: none !important;
                    }

                    .table-card {
                        border: 0;
                        border-radius: 0;
                    }

                    .table-scroll {
                        max-height: none;
                        overflow: visible;
                    }

                    .attendance-table th {
                        position: static;
                    }

                    .employee-header,
                    .employee-cell {
                        position: static !important;
                    }

                    .attendance-status {
                        print-color-adjust: exact;
                        -webkit-print-color-adjust: exact;
                    }
                }

            `}</style>

        </div>
    );
};

export default EmployeeAttendanceReport;
